import { RootState } from "@services/Redux";
import { Money } from "@utils/currency";
import { CnpjDiscountType, DocumentType } from "@utils/types";
import { useFormikContext } from "formik";
import * as React from "react";
import { useSelector } from "react-redux";
import Styled from "./styled";
import { CustomerDataFormValues } from "./types";

const OrderValueSummary: React.FC = () => {
  const companyData = useSelector((state: RootState) => state.company.data);
  const order = useSelector((state: RootState) => state.order.data);
  const values = useFormikContext<CustomerDataFormValues>().values;

  const cnpjDiscountValue = React.useMemo(
    () =>
      companyData.cnpj_discount_type === CnpjDiscountType.FIXED
        ? Money(companyData.cnpj_discount_value ?? 0).value
        : Money(order?.value ?? 0)
            .multiply(companyData.cnpj_discount_percentage ?? 0)
            .divide(100).value,
    // eslint-disable-next-line
    [order]
  );

  const cashPaymentDiscount = React.useMemo(() => {
    return Money(order?.value ?? 0)
      .subtract(values.document_type === DocumentType.CNPJ && (companyData.cnpj_discount_value || companyData.cnpj_discount_percentage) ? cnpjDiscountValue : 0)
      .multiply(companyData.cash_payment_discount_percentage ?? 0)
      .divide(100).value;
    // eslint-disable-next-line
  }, [order, cnpjDiscountValue, values.document_type]);

  const orderValue = React.useMemo(() => {
    return Money(order?.value ?? 0)
      .subtract(values.cash_payment && companyData.cash_payment_discount_percentage ? cashPaymentDiscount : 0)
      .subtract(values.document_type === DocumentType.CNPJ ? cnpjDiscountValue : 0).value;
    // eslint-disable-next-line
  }, [order, cashPaymentDiscount, cnpjDiscountValue, companyData.cash_payment_discount_percentage, values]);

  const couponDiscountsValue = React.useMemo(() => {
    let couponDiscountValue = 0;
    if (!order) return couponDiscountValue;
    if (!order.coupon) return couponDiscountValue;
    couponDiscountValue = Money(couponDiscountValue).add(order.coupon.discount_fixed_value ?? 0).value;
    couponDiscountValue = Money(couponDiscountValue).add(Money(orderValue).multiply((order.coupon.discount_percentage ?? 0) / 100)).value;
    if (order.coupon.max_discount_value > 0 && couponDiscountValue > order.coupon.max_discount_value) couponDiscountValue = order.coupon.max_discount_value;
    return couponDiscountValue;
  }, [order, orderValue]);

  return (
    <Styled.ValuesSummaryContainer className={"border-top border-bottom my-3 py-1"}>
      <table>
        <tbody>
          <tr>
            <td className={"text-end"}>Total de itens: </td>
            <td className={"ps-3 text-end"}>{Money(order?.value ?? 0).format()}</td>
          </tr>
          {values.document_type === DocumentType.CNPJ && (companyData.cnpj_discount_value || companyData.cnpj_discount_percentage) ? (
            <tr>
              <td className={"text-end"}>Desconto de CNPJ: </td>
              <td className={"ps-3 text-end"}>-{Money(cnpjDiscountValue).format()}</td>
            </tr>
          ) : null}
          {values.cash_payment && companyData.cash_payment_discount_percentage ? (
            <tr>
              <td className={"text-end"}>Desconto para pagamento à vista: </td>
              <td className={"ps-3 text-end"}>-{Money(cashPaymentDiscount).format()}</td>
            </tr>
          ) : null}
          {order?.coupon ? (
            <tr>
              <td className={"text-end"}>Cupom de desconto ({order.coupon.code}): </td>
              <td className={"ps-3 text-end"}>-{Money(couponDiscountsValue).format()}</td>
            </tr>
          ) : null}
          <tr>
            <td className={"text-end"}>
              <b>Total a pagar: </b>
            </td>
            <td className={"ps-3 text-end"}>
              <b>
                {Money(orderValue)
                  .subtract(couponDiscountsValue ?? 0)
                  .format()}
              </b>
            </td>
          </tr>
        </tbody>
      </table>
    </Styled.ValuesSummaryContainer>
  );
};

export default OrderValueSummary;
